import type { ReactElement, ReactNode } from 'react';
import { AlertCircle, Server } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { SiemProtocol } from '@kirin/api-client/contracts/settings';
import type {
  SettingResponse,
  SiemConfig,
} from '@kirin/api-client/contracts/settings';

import { IntegrationCard } from './IntegrationCard';
import { renderDefaultActions } from './defaultActions';

export interface SiemIntegrationCardProps {
  siemSettings: SettingResponse | null | undefined;
  onConnect?: () => void;
  onManage?: () => void;
  onDisconnect?: () => void;
  isLoading?: boolean;
  error?: string;
  readOnly?: boolean;
}

function buildConnectionSummary(
  siemSettings: SettingResponse | null | undefined
): string | null {
  if (siemSettings?.enabled !== true) return null;
  const { protocol, host, port, endpointUrl } = (siemSettings.config ??
    {}) as Partial<
    Pick<SiemConfig, 'protocol' | 'host' | 'port' | 'endpointUrl'>
  >;
  // HEC has no host/port — surface the endpoint host so a connected HEC org
  // still sees where events go (GiorgeCh review).
  if (protocol === SiemProtocol.Hec) {
    if (!endpointUrl) return null;
    let label = endpointUrl;
    try {
      label = new URL(endpointUrl).host;
    } catch {
      // Fall back to the raw URL if it somehow doesn't parse.
    }
    return `HEC ${label}`;
  }
  if (!protocol || !host || !port) return null;
  return `${protocol.toUpperCase()} ${host}:${port}`;
}

function renderMeta(summary: string | null): ReactNode {
  if (summary === null) return null;
  return (
    <IntegrationCard.Meta>
      <span data-testid="siem-meta-connection">{summary}</span>
    </IntegrationCard.Meta>
  );
}

function renderSiemErrorAlert(error: string | undefined): ReactNode {
  if (!error) return null;
  return (
    <IntegrationCard.Body>
      <Alert data-testid="siem-error-alert" variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    </IntegrationCard.Body>
  );
}

function renderSiemErrorPill(error: string | undefined): ReactNode {
  if (!error) return null;
  return (
    <IntegrationCard.StatusBadge>
      <Badge
        data-testid="siem-error-pill"
        variant="destructive"
        className="gap-1.5"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Error
      </Badge>
    </IntegrationCard.StatusBadge>
  );
}

export function SiemIntegrationCard(
  props: SiemIntegrationCardProps
): ReactElement {
  const {
    siemSettings,
    onConnect,
    onManage,
    onDisconnect,
    isLoading,
    error,
    readOnly,
  } = props;
  const connected = siemSettings?.enabled === true;
  const summary = buildConnectionSummary(siemSettings);

  return (
    <IntegrationCard
      icon={<Server size={20} aria-hidden />}
      title="SIEM"
      subtitle="Forward security events to your SIEM for centralized monitoring"
      status={connected ? 'connected' : 'not_configured'}
      isLoading={isLoading}
    >
      {renderSiemErrorPill(error)}
      {renderMeta(summary)}
      {renderSiemErrorAlert(error)}
      {renderDefaultActions(
        connected,
        { onConnect, onManage, onDisconnect },
        readOnly
      )}
    </IntegrationCard>
  );
}

SiemIntegrationCard.displayName = 'SiemIntegrationCard';
